import { PrintDocument } from './document'
import { escPosDrawerKick, HtmlOptions, render } from './render'
import { resolveParams } from './params'
import { printHtml, sendHttp, sendImin } from './transports/other'
import { sendSerial } from './transports/webserial'
import { sendUsb } from './transports/webusb'
import { DeviceModel, DeviceRole, DeviceSetup, PairedDevice } from './types'

/** A paired device with its catalog model and merged params. */
export interface ResolvedDevice {
  device: PairedDevice
  model: DeviceModel
  params: Record<string, unknown>
}

export const resolveDevice = (device: PairedDevice, models: DeviceModel[]): ResolvedDevice | null => {
  const model = models.find((m) => m._id === device.model_id)
  return model ? { device, model, params: resolveParams(model.protocol, model.params, device.params) } : null
}

/** The devices assigned to a role, in the order set on the till. */
export const devicesFor = (setup: DeviceSetup, role: DeviceRole, models: DeviceModel[]): ResolvedDevice[] =>
  (setup.roles[role] ?? [])
    .map((id) => setup.devices.find((d) => d.id === id))
    .filter((d): d is PairedDevice => !!d)
    .map((d) => resolveDevice(d, models))
    .filter((d): d is ResolvedDevice => !!d)

/** Renders a document in the device's protocol and sends it over the device's transport. */
export const printTo = async (target: ResolvedDevice, doc: PrintDocument, html: HtmlOptions = {}) => {
  const { device, model, params } = target
  const out = render(model.protocol, doc, params, html)
  if (device.transport === 'browser') {
    if (!('text' in out) || out.mime !== 'text/html') throw new Error(`${model.name} cannot print through the browser`)
    return printHtml(out.text)
  }
  const bytes = 'bytes' in out ? out.bytes : new TextEncoder().encode(out.text)
  if (device.transport === 'webusb' && device.handle?.usb) return sendUsb(device.handle.usb, bytes)
  if (device.transport === 'webserial' && device.handle?.serial) return sendSerial(device.handle.serial, bytes, Number(params.baud_rate ?? 9600))
  if (device.transport === 'imin') return sendImin(bytes)
  if (device.transport === 'http' && device.handle?.http) return sendHttp(device.handle.http, bytes)
  throw new Error(`${device.name} is not connected`)
}

/** Opens the drawer through the printer it hangs off (ESC/POS pulse; Star uses BEL). */
export const kickDrawer = async (target: ResolvedDevice) => {
  const { model, params } = target
  if (model.protocol === 'escpos') return printRaw(target, escPosDrawerKick(params))
  if (model.protocol === 'star') return printRaw(target, Uint8Array.from([0x07]))
  throw new Error(`${target.device.name} cannot open a cash drawer`)
}

const printRaw = async ({ device, params }: ResolvedDevice, bytes: Uint8Array) => {
  if (device.transport === 'webusb' && device.handle?.usb) return sendUsb(device.handle.usb, bytes)
  if (device.transport === 'webserial' && device.handle?.serial) return sendSerial(device.handle.serial, bytes, Number(params.baud_rate ?? 9600))
  if (device.transport === 'imin') return sendImin(bytes)
  if (device.transport === 'http' && device.handle?.http) return sendHttp(device.handle.http, bytes)
  throw new Error(`${device.name} is not connected`)
}
